import React, { useState, useEffect, useCallback } from "react";
import { RotateCcw, Trash2, Calendar, User, Eye, AlertCircle } from "lucide-react";
import { trashService, DeletedPost } from "../../services/trashService";
import { formatDateTime } from "../../utils/dateFormatter";
import TrashDetailModal from "../TrashDetailModal";
import "../../styles/components/deleted-posts.css";

const DeletedPosts: React.FC = () => {
  const [posts, setPosts] = useState<DeletedPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPosts, setSelectedPosts] = useState<number[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(10);
  const [selectedPost, setSelectedPost] = useState<DeletedPost | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const loadPosts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await trashService.getDeletedPosts(page, limit);
      setPosts(response.data);
      setTotal(response.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Có lỗi xảy ra khi tải dữ liệu');
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const handleRestore = async (postId: number) => {
    try {
      await trashService.restorePost(postId);
      setPosts(posts.filter(post => post.id !== postId));
      setSelectedPosts(prev => prev.filter(id => id !== postId));
      setTotal(prev => prev - 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể khôi phục bài viết');
    }
  };

  const handlePermanentDelete = async (postId: number) => {
    try {
      await trashService.permanentDeletePost(postId);
      setPosts(posts.filter(post => post.id !== postId));
      setSelectedPosts(prev => prev.filter(id => id !== postId));
      setTotal(prev => prev - 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể xóa vĩnh viễn bài viết');
    }
  };

  const handleBulkRestore = async () => {
    try {
      await trashService.bulkRestorePosts(selectedPosts);
      setPosts(posts.filter(post => !selectedPosts.includes(post.id)));
      setSelectedPosts([]);
      setTotal(prev => prev - selectedPosts.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể khôi phục các bài viết');
    }
  };

  const handleBulkDelete = async () => {
    try {
      await trashService.bulkDeletePosts(selectedPosts);
      setPosts(posts.filter(post => !selectedPosts.includes(post.id)));
      setSelectedPosts([]);
      setTotal(prev => prev - selectedPosts.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể xóa vĩnh viễn các bài viết');
    }
  };

  const toggleSelectPost = (postId: number) => {
    setSelectedPosts(prev => 
      prev.includes(postId) 
        ? prev.filter(id => id !== postId)
        : [...prev, postId]
    );
  };

  const selectAll = () => {
    setSelectedPosts(posts.map(post => post.id));
  };

  const deselectAll = () => {
    setSelectedPosts([]);
  };

  const handleViewDetails = (post: DeletedPost) => {
    setSelectedPost(post);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedPost(null);
  };

  if (loading) {
    return (
      <div className="trash-loading">
        <div className="trash-loading__spinner"></div>
        <p>Đang tải danh sách bài viết đã xóa...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="trash-empty">
        <AlertCircle size={48} className="trash-empty__icon" />
        <h3 className="trash-empty__title">Lỗi tải dữ liệu</h3>
        <p className="trash-empty__description">{error}</p>
        <button onClick={loadPosts} className="trash-empty__retry">
          Thử lại
        </button>
      </div>
    );
  }

  return (
    <div className="deleted-posts">
      <div className="deleted-posts__header">
        <div className="deleted-posts__info">
          <h3 className="deleted-posts__title">Bài viết đã xóa ({posts.length})</h3>
          <p className="deleted-posts__description">Danh sách các bài viết đã bị xóa và có thể khôi phục</p>
        </div>
        
        {posts.length > 0 && (
          <div className="deleted-posts__bulk-actions">
            <button 
              className="deleted-posts__bulk-btn deleted-posts__bulk-btn--restore"
              onClick={handleBulkRestore}
              disabled={selectedPosts.length === 0}
            >
              <RotateCcw size={16} />
              Khôi phục ({selectedPosts.length})
            </button>
            <button 
              className="deleted-posts__bulk-btn deleted-posts__bulk-btn--delete"
              onClick={handleBulkDelete}
              disabled={selectedPosts.length === 0}
            >
              <Trash2 size={16} />
              Xóa vĩnh viễn ({selectedPosts.length})
            </button>
            <button 
              className="deleted-posts__bulk-btn deleted-posts__bulk-btn--select"
              onClick={selectedPosts.length === posts.length ? deselectAll : selectAll}
            >
              {selectedPosts.length === posts.length ? "Bỏ chọn tất cả" : "Chọn tất cả"}
            </button>
          </div>
        )}
      </div>

      {posts.length === 0 ? (
        <div className="trash-empty">
          <Trash2 size={48} className="trash-empty__icon" />
          <h3 className="trash-empty__title">Không có bài viết nào trong thùng rác</h3>
          <p className="trash-empty__description">Tất cả bài viết đều đang hoạt động bình thường</p>
        </div>
      ) : (
        <div className="deleted-posts__list">
          {posts.map((post) => (
            <div key={post.id} className="deleted-posts__item">
              <div className="deleted-posts__checkbox">
                <input
                  type="checkbox"
                  checked={selectedPosts.includes(post.id)}
                  onChange={() => toggleSelectPost(post.id)}
                />
              </div>
              
              <div className="deleted-posts__content">
                <div className="deleted-posts__item-header">
                  <h4 className="deleted-posts__item-title">{post.title}</h4>
                  <span className="deleted-posts__item-status deleted-posts__item-status--deleted">Đã xóa</span>
                </div>
                
                <div className="deleted-posts__meta">
                  <div className="deleted-posts__meta-item">
                    <User size={14} />
                    <span>Tác giả: {post.author}</span>
                  </div>
                  <div className="deleted-posts__meta-item">
                    <Calendar size={14} />
                    <span>Xóa lúc: {formatDateTime(post.deletedAt)}</span>
                  </div>
                  <div className="deleted-posts__meta-item">
                    <span>Xóa bởi: {post.deletedBy}</span>
                  </div>
                  <div className="deleted-posts__meta-item">
                    <span>Danh mục: {post.category}</span>
                  </div>
                </div>
                
                <div className="deleted-posts__preview">
                  {post.content.substring(0, 150)}...
                </div>
              </div>
              
              <div className="deleted-posts__actions">
                <button 
                  className="deleted-posts__action-btn deleted-posts__action-btn--preview"
                  onClick={() => handleViewDetails(post)}
                  title="Xem chi tiết"
                >
                  <Eye size={16} />
                </button>
                <button 
                  className="deleted-posts__action-btn deleted-posts__action-btn--restore"
                  onClick={() => handleRestore(post.id)}
                  title="Khôi phục"
                >
                  <RotateCcw size={16} />
                </button>
                <button 
                  className="deleted-posts__action-btn deleted-posts__action-btn--delete"
                  onClick={() => handlePermanentDelete(post.id)}
                  title="Xóa vĩnh viễn"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Pagination */}
      {total > limit && (
        <div className="deleted-posts__pagination">
          <button 
            onClick={() => setPage(prev => Math.max(1, prev - 1))}
            disabled={page === 1}
            className="deleted-posts__pagination-btn"
          >
            Trước
          </button>
          <span className="deleted-posts__pagination-info">
            Trang {page} / {Math.ceil(total / limit)}
          </span>
          <button 
            onClick={() => setPage(prev => prev + 1)}
            disabled={page >= Math.ceil(total / limit)}
            className="deleted-posts__pagination-btn"
          >
            Sau
          </button>
        </div>
      )}
      
      {/* Detail Modal */}
      <TrashDetailModal
        isOpen={isDetailModalOpen}
        onClose={handleCloseDetailModal}
        item={selectedPost}
        type="post"
      />
    </div>
  );
};

export default DeletedPosts;
